/**
 * `useIntelWidth` — the width an Analysis chart lays out at (#749).
 *
 * The caller's `w` wins (a test, or a call site that already knows); otherwise the chart's
 * own `svg`, drawn `width="100%"`, is measured with `useChartWidth`. In the lightbox the
 * measured width is divided by `INTEL_TEXT_ZOOM`, so the dialog's copy prints its 11 px text
 * a little larger than the panel does. Where nothing measures (jsdom) the chart keeps the
 * width it always had, `fallback`.
 */
import type { RefObject } from 'react';

import { useChartWidth, useChartZoom } from '../../../components/charts/useChartWidth';
import { INTEL_TEXT_ZOOM } from './labels';

/** The smallest width any of these charts lays out at; narrower boxes scale it down. */
export const MIN_CHART_W = 200;

export function useIntelWidth(ref: RefObject<Element | null>, w: number | undefined, fallback: number): number {
  const zoom = useChartZoom();
  const measured = useChartWidth(ref, fallback, { zoom: zoom > 1 ? INTEL_TEXT_ZOOM : 1 });
  const width = typeof w === 'number' && w > 0 ? w : measured;
  return Math.max(MIN_CHART_W, Math.round(width));
}
